import { orderBlocksForPayload } from '@/lib/blocks/tree';
import { readRecord } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';
import {
  PageEditorActionType,
  type PageEditorAddBlockAction,
  type PageEditorDeleteBlockAction,
  type PageEditorUpdateBlockAction,
} from '@/types/page-editor-actions';
import type { PageSection } from '@/types/page-section';

type AutosaveBlockShape = Omit<PageBlock, 'uiKey'>;
type AutosaveAddBlockPayload = AutosaveBlockShape & { operation: 'add' };
type AutosaveUpdateBlockPayload = Partial<Omit<AutosaveBlockShape, 'id'>> & {
  id: string;
  operation: 'update';
};
type AutosaveDeleteBlockPayload = { id: string; operation: 'delete' };
export type AutosaveBlockPayload =
  | AutosaveAddBlockPayload
  | AutosaveUpdateBlockPayload
  | AutosaveDeleteBlockPayload;

type LifecycleFirstMutation = 'add' | 'delete' | 'update';

export function mergeBlockUpdates(
  existing: Partial<PageBlock>,
  next: Partial<PageBlock>,
): Partial<PageBlock> {
  const { id: _ignoredId, uiKey: _ignoredUiKey, ...nextWithoutId } = next;
  const merged: Partial<PageBlock> = {
    ...existing,
    ...nextWithoutId,
  };

  if (readRecord(existing.props) && readRecord(nextWithoutId.props)) {
    merged.props = {
      ...existing.props,
      ...nextWithoutId.props,
    };
  } else if ('props' in nextWithoutId && !readRecord(nextWithoutId.props)) {
    delete merged.props;
  }

  return merged;
}

export function buildBlockPayload({
  section,
  isSectionAdd,
  actions,
}: {
  section: PageSection;
  isSectionAdd: boolean;
  actions: Array<
    PageEditorAddBlockAction | PageEditorUpdateBlockAction | PageEditorDeleteBlockAction
  >;
}): AutosaveBlockPayload[] | undefined {
  const blocks = orderBlocksForPayload(section.blocks || []);

  if (isSectionAdd) {
    return blocks.map((block) => ({
      id: block.id,
      blockType: block.blockType,
      props: block.props,
      parentBlockId: block.parentBlockId,
      columnSpan: block.columnSpan,
      position: block.position,
      visible: block.visible,
      operation: 'add',
    }));
  }

  if (actions.length === 0) {
    return undefined;
  }

  const blockMutations = new Map<
    string,
    {
      firstMutation: LifecycleFirstMutation;
      operation: 'add' | 'update' | 'delete' | 'none';
      updates: Partial<PageBlock>;
    }
  >();

  actions.forEach((action) => {
    const blockId = action.payload.blockId;
    const existing = blockMutations.get(blockId);
    const firstMutation =
      existing?.firstMutation ??
      (action.type === PageEditorActionType.PAGE_BLOCK_ADD
        ? 'add'
        : action.type === PageEditorActionType.PAGE_BLOCK_DELETE
          ? 'delete'
          : 'update');
    const createdInCurrentBatch = firstMutation === 'add';

    if (action.type === PageEditorActionType.PAGE_BLOCK_ADD) {
      if (existing?.operation === 'delete' && !createdInCurrentBatch) {
        // Undo-of-delete for a pre-existing block: keep any accumulated
        // updates, otherwise drop the mutation so nothing is sent.
        blockMutations.set(blockId, {
          firstMutation,
          operation: Object.keys(existing.updates).length > 0 ? 'update' : 'none',
          updates: existing.updates,
        });
        return;
      }

      blockMutations.set(blockId, {
        firstMutation,
        operation: 'add',
        updates: {},
      });
      return;
    }

    if (action.type === PageEditorActionType.PAGE_BLOCK_UPDATE) {
      if (!existing) {
        blockMutations.set(blockId, {
          firstMutation,
          operation: 'update',
          updates: mergeBlockUpdates({}, action.payload.updates),
        });
        return;
      }

      if (existing.operation === 'add') {
        blockMutations.set(blockId, {
          ...existing,
          updates: mergeBlockUpdates(existing.updates, action.payload.updates),
        });
        return;
      }

      if (existing.operation === 'delete') {
        return;
      }

      blockMutations.set(blockId, {
        firstMutation,
        operation: 'update',
        updates: mergeBlockUpdates(existing.updates, action.payload.updates),
      });
      return;
    }

    if (action.type === PageEditorActionType.PAGE_BLOCK_DELETE) {
      if (existing?.operation === 'add' && createdInCurrentBatch) {
        blockMutations.set(blockId, {
          firstMutation,
          operation: 'none',
          updates: {},
        });
        return;
      }

      blockMutations.set(blockId, {
        firstMutation,
        operation: 'delete',
        // Keep any earlier updates that happened before this delete when the
        // block already existed before the batch. If the delete is undone later
        // in the same batch (DELETE -> ADD for the same persisted id), we need
        // those preserved updates so the net mutation can become UPDATE instead
        // of incorrectly collapsing to "nothing changed".
        updates: existing?.updates || {},
      });
    }
  });

  const payload: AutosaveBlockPayload[] = [];

  blocks.forEach((block) => {
    const mutation = blockMutations.get(block.id);
    if (!mutation) {
      return;
    }

    if (mutation.operation === 'add') {
      const { uiKey: _uiKey, ...blockWithoutUiKey } = block;
      const normalizedBlock = readRecord(block.props)
        ? blockWithoutUiKey
        : {
            ...blockWithoutUiKey,
            props: {},
          };

      payload.push({
        ...normalizedBlock,
        operation: 'add',
      });
      return;
    }

    if (mutation.operation === 'update') {
      if (Object.keys(mutation.updates).length === 0) {
        return;
      }

      const { props: rawProps, ...updatesWithoutProps } = mutation.updates;
      const accumulatedProps = readRecord(rawProps);
      const normalizedUpdates = {
        ...updatesWithoutProps,
        ...(accumulatedProps ? { props: accumulatedProps } : {}),
      };

      payload.push({
        id: block.id,
        operation: 'update',
        ...normalizedUpdates,
      });
    }
  });

  blockMutations.forEach((mutation, blockId) => {
    if (mutation.operation !== 'delete') {
      return;
    }

    payload.push({
      id: blockId,
      operation: 'delete',
    });
  });

  return payload.length > 0 ? payload : undefined;
}
